import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  StarIcon,
  DocumentTextIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';

interface JobApplication {
  id: string;
  jobId: string;
  studentId: string;
  jobTitle: string;
  applicantName: string;
  applicantEmail: string;
  appliedAt: Date | string | { _seconds: number; _nanoseconds: number };
  status: 'pending' | 'reviewed' | 'interview' | 'accepted' | 'rejected';
  coverLetter: string;
  resumeUrl?: string;
  portfolioUrl?: string;
  matchScore: number;
  relevantCertifications: string[];
  feedback?: string;
  applicantProfile?: {
    skills: string[];
    education: any[];
    experience: any[];
    courseStats: {
      completed: number;
      enrolled: number;
      certificationsEarned: number;
      averageScore: number;
    };
    skillRatings: any[];
  };
}

const ApplicationsPage: React.FC = () => {
  const { userProfile, getToken } = useAuth();
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [feedbackText, setFeedbackText] = useState('');

  useEffect(() => {
    fetchApplications();
  }, [statusFilter]);

  // Helper function to format Firebase timestamps
  const formatFirebaseDate = (timestamp: Date | string | { _seconds: number; _nanoseconds: number }) => {
    if (typeof timestamp === 'string') {
      return new Date(timestamp).toLocaleDateString();
    } else if (timestamp && typeof timestamp === 'object' && '_seconds' in timestamp) {
      return new Date(timestamp._seconds * 1000).toLocaleDateString();
    } else if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString();
    }
    return 'Unknown date';
  };

  // Helper function to get proper applicant name
  const getApplicantName = (application: JobApplication): string => {
    // First try the applicantName from the application
    if (application.applicantName && application.applicantName.trim() !== '' && application.applicantName !== 'Unknown') {
      return application.applicantName;
    }

    // If applicantProfile exists, try to build name from there
    if (application.applicantProfile) {
      const profile = application.applicantProfile as any;
      if (profile.displayName && profile.displayName.trim() !== '') {
        return profile.displayName;
      }
      if (profile.firstName && profile.lastName) {
        return `${profile.firstName} ${profile.lastName}`.trim();
      }
      if (profile.firstName) {
        return profile.firstName;
      }
    }

    // Fallback to email username if available
    if (application.applicantEmail) {
      const emailUsername = application.applicantEmail.split('@')[0];
      return emailUsername.charAt(0).toUpperCase() + emailUsername.slice(1);
    }

    // Final fallback
    return 'Unknown Applicant';
  };

  const fetchApplications = async () => {
    try {
      setLoading(true);
      
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/employer/applications?status=${statusFilter}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setApplications(result.data.applications || []);
      } else {
        throw new Error(result.message || 'Failed to fetch applications');
      }
    } catch (error: any) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
      
      // Fallback to empty data
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (applicationId: string, status: string, feedback?: string) => {
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs/applications/${applicationId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status, feedback })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setApplications(prev => 
          prev.map(app => 
            app.id === applicationId 
              ? { ...app, status: status as any, feedback }
              : app
          )
        );
        
        toast.success(`Application ${status} successfully`);
        setSelectedApplication(null);
        setFeedbackText('');
      } else {
        throw new Error(result.message || 'Failed to update application status');
      }
    } catch (error: any) {
      console.error('Error updating application status:', error);
      toast.error('Failed to update application status');
    }
  };

  const startConversation = async (application: JobApplication) => {
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/messages/conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipientId: application.studentId,
          jobId: application.jobId,
          applicationId: application.id,
          initialMessage: `Hi ${getApplicantName(application).split(' ')[0]}, thank you for your interest in the ${application.jobTitle} position. I'd like to discuss your application further.`
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Navigate to messages page or open messaging modal
        window.location.href = `/messages/${result.data.conversationId}`;
        toast.success('Conversation started successfully');
      } else {
        throw new Error(result.message || 'Failed to start conversation');
      }
    } catch (error: any) {
      console.error('Error starting conversation:', error);
      toast.error('Failed to start conversation');
    }
  };

  const filteredApplications = applications.filter(app => 
    statusFilter === 'all' || app.status === statusFilter
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'reviewing': return 'bg-blue-100 text-blue-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getMatchScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Job Applications</h1>
          <p className="mt-2 text-gray-600">
            Review and manage applications from candidates
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white rounded-lg shadow p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Applications</option>
                <option value="pending">Pending</option>
                <option value="reviewing">Under Review</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Applications List */}
        {filteredApplications.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No Applications</h3>
            <p className="mt-2 text-gray-500">
              {statusFilter === 'all' 
                ? "You haven't received any applications yet." 
                : `No applications with status "${statusFilter}".`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((application) => (
              <div key={application.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {getApplicantName(application)}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                          {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                        <span className="flex items-center gap-1">
                          <DocumentTextIcon className="h-4 w-4" />
                          {application.jobTitle}
                        </span>
                        <span className="flex items-center gap-1">
                          <ClockIcon className="h-4 w-4" />
                          {formatFirebaseDate(application.appliedAt)}
                        </span>
                        <span className={`flex items-center gap-1 font-medium ${getMatchScoreColor(application.matchScore)}`}>
                          <StarIcon className="h-4 w-4" />
                          {application.matchScore}% Match
                        </span>
                      </div>

                      <p className="text-gray-700 mb-3 line-clamp-2">
                        {application.coverLetter}
                      </p>

                      {application.relevantCertifications.length > 0 && (
                        <div className="flex items-center gap-2 mb-3">
                          <AcademicCapIcon className="h-4 w-4 text-gray-400" />
                          <div className="flex flex-wrap gap-1">
                            {application.relevantCertifications.slice(0, 3).map((cert, index) => (
                              <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                {cert}
                              </span>
                            ))}
                            {application.relevantCertifications.length > 3 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                +{application.relevantCertifications.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => setSelectedApplication(application)}
                        className="flex items-center gap-1 px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                      >
                        <EyeIcon className="h-4 w-4" />
                        Review
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Application Review Modal */}
        {selectedApplication && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Review Application
                </h3>
                <button
                  onClick={() => {
                    setSelectedApplication(null);
                    setFeedbackText('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Applicant Details</h4>
                  <div className="bg-gray-50 p-3 rounded">
                    <p><strong>Name:</strong> {getApplicantName(selectedApplication)}</p>
                    <p><strong>Email:</strong> {selectedApplication.applicantEmail}</p>
                    <p><strong>Match Score:</strong> 
                      <span className={`ml-1 font-medium ${getMatchScoreColor(selectedApplication.matchScore)}`}>
                        {selectedApplication.matchScore}%
                      </span>
                    </p>
                  </div>
                </div>

                {/* Enhanced Candidate Profile */}
                {selectedApplication.applicantProfile && (
                  <div className="space-y-4">
                    {/* Course Performance */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Academic Performance</h4>
                      <div className="bg-gray-50 p-3 rounded">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p><strong>Courses Completed:</strong> {selectedApplication.applicantProfile.courseStats?.completed || 0}</p>
                            <p><strong>Currently Enrolled:</strong> {selectedApplication.applicantProfile.courseStats?.enrolled || 0}</p>
                          </div>
                          <div>
                            <p><strong>Certifications Earned:</strong> {selectedApplication.applicantProfile.courseStats?.certificationsEarned || 0}</p>
                            <p><strong>Average Score:</strong> {selectedApplication.applicantProfile.courseStats?.averageScore || 0}%</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Skills */}
                    {selectedApplication.applicantProfile.skills && selectedApplication.applicantProfile.skills.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Skills</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedApplication.applicantProfile.skills.map((skill, index) => (
                            <span key={index} className="px-3 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-full">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Education */}
                    {selectedApplication.applicantProfile.education && selectedApplication.applicantProfile.education.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Education</h4>
                        <div className="bg-gray-50 p-3 rounded space-y-2">
                          {selectedApplication.applicantProfile.education.map((edu, index) => (
                            <div key={index} className="text-sm">
                              <p><strong>{edu.degree || 'Degree'}</strong> - {edu.institution || 'Institution'}</p>
                              <p className="text-gray-600">{edu.field || 'Field of Study'} • {edu.year || 'Year'}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Experience */}
                    {selectedApplication.applicantProfile.experience && selectedApplication.applicantProfile.experience.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Experience</h4>
                        <div className="bg-gray-50 p-3 rounded space-y-2">
                          {selectedApplication.applicantProfile.experience.map((exp, index) => (
                            <div key={index} className="text-sm">
                              <p><strong>{exp.title || 'Position'}</strong> at {exp.company || 'Company'}</p>
                              <p className="text-gray-600">{exp.duration || 'Duration'}</p>
                              {exp.description && <p className="text-gray-700 mt-1">{exp.description}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Cover Letter</h4>
                  <div className="bg-gray-50 p-3 rounded max-h-32 overflow-y-auto">
                    <p className="text-gray-700">{selectedApplication.coverLetter}</p>
                  </div>
                </div>

                {selectedApplication.relevantCertifications.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Relevant Certifications</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedApplication.relevantCertifications.map((cert, index) => (
                        <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Feedback (Optional)</h4>
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Add feedback for the applicant..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div className="flex gap-3 pt-4 flex-wrap">
                  <button
                    onClick={() => updateApplicationStatus(selectedApplication.id, 'accepted', feedbackText)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    <CheckCircleIcon className="h-4 w-4" />
                    Accept
                  </button>
                  <button
                    onClick={() => updateApplicationStatus(selectedApplication.id, 'rejected', feedbackText)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    <XCircleIcon className="h-4 w-4" />
                    Reject
                  </button>
                  <button
                    onClick={() => updateApplicationStatus(selectedApplication.id, 'interview', feedbackText)}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                  >
                    <ClockIcon className="h-4 w-4" />
                    Schedule Interview
                  </button>
                  <button
                    onClick={() => updateApplicationStatus(selectedApplication.id, 'reviewed', feedbackText)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <EyeIcon className="h-4 w-4" />
                    Mark as Reviewed
                  </button>
                  <button
                    onClick={() => startConversation(selectedApplication)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                  >
                    <ChatBubbleLeftIcon className="h-4 w-4" />
                    Message Candidate
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationsPage;